import { createFileRoute } from '@tanstack/react-router'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useTimeline } from '@/api/hooks/useTimeline'
import { StateSwitcher } from '@/components/atoms/state-switcher'
import { EmptyState } from '@/components/molecules/empty-state'
import PageHeader from '@/components/molecules/page-header'
import { PlayVideoButton } from '@/components/molecules/play-video-button'
import TimelineVertical, {
  ExtendedTimelineEvent,
} from '@/components/organisms/timeline/timeline-vertical'
import { TIMELINE_VIDEO } from '@/config/videos'
import { useIndividuals } from '@/hooks/useIndividuals'

const TimelinePage = () => {
  const { t } = useTranslation()
  const { selectedIndividualId } = useIndividuals()
  const { data: timeline, isError, isLoading } = useTimeline(selectedIndividualId)

  const eventsWithDocuments = useMemo((): ExtendedTimelineEvent[] => {
    if (!timeline?.events) return []

    return timeline.events.map(event => ({
      ...event,
      documents:
        event.documents?.map(documentId => timeline.documents[documentId]!).filter(Boolean) ?? [],
    }))
  }, [timeline])

  return (
    <div className="space-y-8">
      <PageHeader
        body={t('timelinePage.header.description')}
        heading={t('timelinePage.header.title')}
        titleAction={
          <PlayVideoButton
            titleKey={TIMELINE_VIDEO.titleKey}
            youtubeId={TIMELINE_VIDEO.youtubeId}
          />
        }
      />
      <StateSwitcher
        emptyState={
          <EmptyState
            body={t('timelinePage.emptyState.description')}
            ctaLink="/documents"
            ctaText={t('timelinePage.emptyState.cta')}
            ctaType="navigate"
            heading={t('timelinePage.emptyState.title')}
          />
        }
        isEmpty={eventsWithDocuments.length === 0}
        isError={isError}
        isLoading={isLoading}
      >
        <div className="bg-layer-1 p-4 rounded-md border">
          <TimelineVertical events={eventsWithDocuments} />
        </div>
      </StateSwitcher>
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/timeline')({
  component: TimelinePage,
})
